var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef } from 'react';
import deepEqual from 'fast-deep-equal';
import { Grid, GridColumn } from "../Grid";
import { GRID_HEIGHT_MODE } from "../Grid/types";
import iconTextCellRenderer from './iconTextCellRenderer';
import { createUIElement } from "../helpers";
export var SELECTION_MODE;
(function (SELECTION_MODE) {
    SELECTION_MODE["single"] = "single";
    SELECTION_MODE["multi"] = "multi";
})(SELECTION_MODE || (SELECTION_MODE = {}));
export var defaultIconGetter = function (option) {
    return option &&
        (typeof option['icon'] === 'string' ||
            React.isValidElement(option['icon']))
        ? option['icon']
        : null;
};
export var defaultTextGetter = function (option) {
    if (option && typeof option['text'] === 'string') {
        return option['text'];
    }
    console.warn("Text not found in 'data' or 'selected' props. Please describe the prop 'textGetter' in 'List'.");
    return '';
};
export var getIndexData = function (data, value, valueGetter, compareValue) {
    var result = -1;
    data.some(function (dataRow, idx) {
        if (compareValue(valueGetter(dataRow), value)) {
            result = idx;
            return true;
        }
        return false;
    });
    return result;
};
export var List = createUIElement('List', function (_a, ref) {
    var className = _a.className, data = _a.data, _b = _a.selected, selected = _b === void 0 ? [] : _b, valueGetter = _a.valueGetter, _c = _a.iconGetter, iconGetter = _c === void 0 ? defaultIconGetter : _c, _d = _a.textGetter, textGetter = _d === void 0 ? defaultTextGetter : _d, _e = _a.comparator, comparator = _e === void 0 ? deepEqual : _e, columnWidth = _a.columnWidth, _f = _a.disabled, disabled = _f === void 0 ? false : _f, isRowDisabled = _a.isRowDisabled, _g = _a.selectionMode, selectionMode = _g === void 0 ? SELECTION_MODE.single : _g, _h = _a.hasCheckbox, hasCheckbox = _h === void 0 ? false : _h, _j = _a.hasSelectionHeader, hasSelectionHeader = _j === void 0 ? true : _j, _k = _a.heightMode, heightMode = _k === void 0 ? GRID_HEIGHT_MODE.auto : _k, onChange = _a.onChange, onClick = _a.onClick, onDoubleClick = _a.onDoubleClick, _l = _a.texts, texts = _l === void 0 ? {
        empty: 'No data'
    } : _l, cellRenderer = _a.cellRenderer, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, onKeyDown = _a.onKeyDown, onKeyUp = _a.onKeyUp, onKeyPress = _a.onKeyPress, style = _a.style, rest = __rest(_a, ["className", "data", "selected", "valueGetter", "iconGetter", "textGetter", "comparator", "columnWidth", "disabled", "isRowDisabled", "selectionMode", "hasCheckbox", "hasSelectionHeader", "heightMode", "onChange", "onClick", "onDoubleClick", "texts", "cellRenderer", "onMouseEnter", "onMouseLeave", "onKeyDown", "onKeyUp", "onKeyPress", "style"]);
    var dataRef = useRef(data);
    dataRef.current = data;
    if (selectionMode === SELECTION_MODE.single && selected.length > 1) {
        console.warn("The prop 'value' must be an array of length equal to 1 in 'List' with selectionMode = SELECTION_MODE.single.");
    }
    var getGridRowItem = function (option) { return ({
        value: valueGetter(option),
        icon: iconGetter(option),
        text: textGetter(option)
    }); };
    var gridData = dataRef.current.map(getGridRowItem);
    var gridSelected = selected.map(function (value) {
        var index = getIndexData(dataRef.current, value, valueGetter, comparator);
        return getGridRowItem(dataRef.current[index]);
    });
    var isGridRowDisabled = function (_a) {
        var rowIndex = _a.rowIndex, rowData = _a.rowData;
        return !!(isRowDisabled === null || isRowDisabled === void 0 ? void 0 : isRowDisabled({
            rowData: dataRef.current[rowIndex],
            rowIndex: rowIndex
        }));
    };
    var selectionChangeHandler = function (_a) {
        var selected = _a.selected;
        onChange === null || onChange === void 0 ? void 0 : onChange(selected.map(function (selectedRowProp) {
            return selectedRowProp.value;
        }));
    };
    var rowClickHandler = function (_a) {
        var rowIndex = _a.rowIndex;
        onClick === null || onClick === void 0 ? void 0 : onClick({
            dataItem: dataRef.current[rowIndex],
            rowIndex: rowIndex
        });
    };
    var rowDoubleClickHandler = function (_a) {
        var rowIndex = _a.rowIndex;
        onDoubleClick === null || onDoubleClick === void 0 ? void 0 : onDoubleClick({
            dataItem: dataRef.current[rowIndex],
            rowIndex: rowIndex
        });
    };
    return (React.createElement(Grid, __assign({}, rest, { "data-selector-id": rest['data-selector-id'], className: "vm-List " + className, disabled: disabled, isRowDisabled: isGridRowDisabled, hideHeader: true, data: gridData, height: heightMode, onSelectionChange: selectionChangeHandler, onRowClick: rowClickHandler, onRowDoubleClick: onDoubleClick ? rowDoubleClickHandler : undefined, selection: {
            multiple: selectionMode === SELECTION_MODE.multi,
            checkbox: hasCheckbox,
            fullLength: gridData.length,
            field: 'value',
            selected: gridSelected,
            subheaderRenderer: hasSelectionHeader ? undefined : null
        }, texts: texts, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onKeyDown: onKeyDown, onKeyUp: onKeyUp, onKeyPress: onKeyPress, ref: ref, style: style }), React.createElement(GridColumn, { width: columnWidth, cell: cellRenderer || iconTextCellRenderer, field: 'text' })));
});
//# sourceMappingURL=List.js.map